import type { ConsolidatedItem } from '@obraplan/core/domain/takeoff/consolidation';
import { ITEM_KIND_LABEL } from '@obraplan/core/domain/takeoff/takeoff-line';
import { measureShortLabel } from '@obraplan/core/domain/takeoff/applies-to';

/**
 * O quantitativo como arquivo — item a item, com o consumo de cada serviço aberto.
 *
 * Sai em CSV com **ponto e vírgula** e números com vírgula decimal: é o que o Excel em
 * português abre com um duplo clique. Vírgula como separador de campo obrigaria quem
 * recebe o arquivo a passar pelo assistente de importação, e planilha que precisa ser
 * preparada para ser aberta é planilha que ninguém abre.
 *
 * Uma linha por **par item × serviço**, e não uma por item com os serviços em colunas: os
 * serviços variam de item para item, e uma tabela com colunas variáveis não é tabela. Com
 * o par em linhas, a mesma exportação alimenta tabela dinâmica, filtro e soma.
 */

/** `1234.5` → `"1.234,5"` — como o Excel em português espera ler. */
function numero(valor: number): string {
  return valor.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
}

/**
 * O campo, protegido.
 *
 * Descrição de material tem ponto e vírgula, aspas e quebra de linha com frequência
 * suficiente para que escapar não seja zelo excessivo — "TUBO PVC 100MM; BARRA 6M" parte
 * a linha em duas e desloca tudo à direita, que é o mesmo defeito silencioso que o leitor
 * de XLSX já teve.
 */
function campo(valor: string | number | undefined): string {
  if (valor === undefined) return '';
  const texto = typeof valor === 'number' ? numero(valor) : valor;
  return /[";\r\n]/.test(texto) ? `"${texto.replace(/"/g, '""')}"` : texto;
}

const CABECALHO = [
  'Código',
  'Material',
  // A classe vem na linha, e não em arquivo separado: é ela que deixa quem abre a
  // planilha filtrar o pedido de compra sem levar hora de pedreiro junto (RN-166).
  'Classe',
  'Unidade',
  'Disciplina',
  'Serviço',
  // Com a peça quando é ela que distingue — "volume de pilar", e nunca só "volume": pilar
  // e viga não se misturam, e a planilha de quem compra concreto precisa ver qual (RN-257).
  'Mede por',
  'Quantidade no serviço',
  'Total do material',
] as const;

export function quantitativoEmCsv(itens: readonly ConsolidatedItem[]): string {
  const linhas = [CABECALHO.join(';')];

  for (const item of itens) {
    for (const uso of item.usages) {
      linhas.push(
        [
          campo(item.code),
          campo(item.description),
          campo(ITEM_KIND_LABEL[item.kind]),
          campo(item.kind === 'MAO_DE_OBRA' ? 'HH' : item.unit),
          campo(uso.disciplineName),
          campo(uso.serviceName),
          campo(measureShortLabel(uso)),
          campo(uso.quantity),
          // O total do material se repete em cada linha dele de propósito: é o que
          // permite filtrar por serviço sem perder de vista o quanto a obra compra.
          campo(item.total),
        ].join(';'),
      );
    }
  }

  // O BOM é o que faz o Excel reconhecer UTF-8 sozinho; sem ele, "ALVENARIA CERÂMICA"
  // chega como "CERÃ‚MICA" e alguém conclui que o sistema não sabe acentuar.
  return `\ufeff${linhas.join('\r\n')}\r\n`;
}

/** `"quantitativo-obra-2026-09-05.csv"` — o nome diz o que é e de quando. */
export function nomeDoArquivo(escopo: string, agora: Date): string {
  const dia = agora.toISOString().slice(0, 10);
  const limpo = escopo
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();
  return `quantitativo-${limpo === '' ? 'obra' : limpo}-${dia}.csv`;
}

/**
 * Entrega o arquivo ao navegador.
 *
 * Vive aqui e não no componente porque é a única parte que toca o DOM, e isolá-la é o que
 * permite testar a geração do CSV sem montar tela nenhuma.
 */
export function baixarCsv(conteudo: string, nome: string): void {
  const url = URL.createObjectURL(new Blob([conteudo], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = nome;
  link.click();
  // Sem revogar, o blob fica preso na memória da aba até alguém recarregar a página.
  URL.revokeObjectURL(url);
}
